import { reactive } from 'vue';
import { toast } from 'vue-sonner';
import type { ExternalToast } from 'vue-sonner';
import { ApiExceptions } from 'src/exceptions';
import type { dynamicObj } from 'src/pages/assets';

export interface NotifyOptions {
  description?: string;
  detail?: string;
  fullDetail?: string;
  duration?: number;
  source?: string;
  id?: string | number;
  action?: { label: string; onClick: () => void };
  cancel?: { label: string; onClick: () => void };
  onDismiss?: () => void;
  onAutoClose?: () => void;
}

export interface ErrorHistoryEntry {
  id: number;
  key: string;
  message: string;
  detail?: string;
  source?: string;
  route: string;
  timestamp: Date;
  lastOccurredAt: Date;
  count: number;
}

const MAX_ERROR_HISTORY = 5;
let errorIdCounter = 0;

export const errorHistory = reactive<ErrorHistoryEntry[]>([]);

export function clearErrorHistory(): void {
  errorHistory.splice(0, errorHistory.length);
  for (const key of dedupeMap.keys()) {
    if (key.startsWith('id:error:') || key.startsWith('content:error:')) {
      dedupeMap.delete(key);
    }
  }
}

function getCurrentRoute(): string {
  try {
    return window.location.hash?.replace('#', '') || window.location.pathname;
  } catch {
    return 'unknown';
  }
}

function getErrorKey(message: string, source?: string): string {
  return source ?? message;
}

function trackError(message: string, detail?: string, source?: string): string {
  const key = getErrorKey(message, source);
  const now = new Date();
  const existingIndex = errorHistory.findIndex((e) => e.key === key);

  if (existingIndex !== -1) {
    const existing = errorHistory[existingIndex];
    existing.count += 1;
    existing.lastOccurredAt = now;
    existing.message = message;
    existing.detail = detail;
    existing.route = getCurrentRoute();
    if (existingIndex !== 0) {
      errorHistory.splice(existingIndex, 1);
      errorHistory.unshift(existing);
    }
    return key;
  }

  errorHistory.unshift({
    id: ++errorIdCounter,
    key,
    message,
    detail,
    source,
    route: getCurrentRoute(),
    timestamp: now,
    lastOccurredAt: now,
    count: 1,
  });
  if (errorHistory.length > MAX_ERROR_HISTORY) {
    errorHistory.pop();
  }
  return key;
}

const DURATIONS: Record<string, number> = {
  success: 4000,
  warning: 6000,
  info: 5000,
  error: 8000,
};

function mergeOptions(
  type: string,
  options?: NotifyOptions,
): ExternalToast {
  const merged: ExternalToast = {
    duration: options?.duration ?? DURATIONS[type],
  };
  if (options?.id !== undefined) merged.id = options.id;
  if (options?.description) merged.description = options.description;
  if (options?.action) {
    merged.action = {
      label: options.action.label,
      onClick: options.action.onClick,
    };
  }
  if (options?.cancel) {
    merged.cancel = {
      label: options.cancel.label,
      onClick: options.cancel.onClick,
    };
  }
  if (options?.onDismiss) merged.onDismiss = options.onDismiss as ExternalToast['onDismiss'];
  if (options?.onAutoClose) merged.onAutoClose = options.onAutoClose as ExternalToast['onAutoClose'];
  return merged;
}

type NotifyType = 'success' | 'error' | 'warning' | 'info';

interface DedupeEntry {
  id: string | number;
  count: number;
  baseMessage: string;
}

const dedupeMap = new Map<string, DedupeEntry>();

export function clearErrorSource(source: string): void {
  for (let i = errorHistory.length - 1; i >= 0; i--) {
    if (errorHistory[i].source === source) {
      errorHistory.splice(i, 1);
    }
  }
  for (const [key, entry] of dedupeMap) {
    if (entry.id === source) {
      toast.dismiss(entry.id);
      dedupeMap.delete(key);
    }
  }
}

function dedupeKey(type: NotifyType, message: string, merged: ExternalToast): string {
  if (merged.id !== undefined) return `id:${type}:${merged.id}`;
  const description = typeof merged.description === 'string' ? merged.description : '';
  return `content:${type}:${message}:${description}`;
}

function withCounter(message: string, count: number): string {
  return count > 1 ? `${message} (x${count})` : message;
}

function emit(type: NotifyType, message: string, merged: ExternalToast): string | number {
  const key = dedupeKey(type, message, merged);
  const existing = dedupeMap.get(key);
  const count = existing ? existing.count + 1 : 1;
  const id = existing?.id ?? merged.id ?? key;

  const userOnDismiss = merged.onDismiss;
  const userOnAutoClose = merged.onAutoClose;
  const cleanup = () => {
    dedupeMap.delete(key);
  };

  const opts: ExternalToast = {
    ...merged,
    id,
    onDismiss: (t) => {
      cleanup();
      userOnDismiss?.(t);
    },
    onAutoClose: (t) => {
      cleanup();
      userOnAutoClose?.(t);
    },
  };

  dedupeMap.set(key, { id, count, baseMessage: message });
  return toast[type](withCounter(message, count), opts);
}

function copyToClipboard(text: string): void {
  navigator.clipboard.writeText(text).catch(() => {
    // Clipboard API may fail if page is not focused or not HTTPS
  });
}

function readAttachedDetail(message: string): string | undefined {
  const carrier = message as unknown as { detail?: unknown };
  return typeof carrier?.detail === 'string' ? carrier.detail : undefined;
}

function readAttachedFullDetail(message: string): string | undefined {
  const carrier = message as unknown as { fullDetail?: unknown };
  return typeof carrier?.fullDetail === 'string' ? carrier.fullDetail : undefined;
}

export const notify = {
  success(message: string, options?: NotifyOptions): string | number {
    return emit('success', message, mergeOptions('success', options));
  },

  error(message: string, detailOrOptions?: string | NotifyOptions, options?: NotifyOptions): string | number {
    let detail: string | undefined;
    let opts: NotifyOptions | undefined;

    if (typeof detailOrOptions === 'string') {
      detail = detailOrOptions;
      opts = options;
    } else if (detailOrOptions && typeof detailOrOptions === 'object') {
      opts = detailOrOptions;
      detail = opts.detail;
    }

    if (!detail) detail = readAttachedDetail(message);
    const fullDetail = opts?.fullDetail ?? readAttachedFullDetail(message) ?? detail;

    const text = String(message);
    const source = opts?.source;
    const key = trackError(text, detail, source);
    const merged = mergeOptions('error', opts);
    merged.id ??= key;
    if (detail) {
      merged.description = detail;
      merged.action = {
        label: 'Copy',
        onClick: () => copyToClipboard(`${text}\n\n${fullDetail ?? detail}`),
      };
    }
    return emit('error', text, merged);
  },

  warning(message: string, options?: NotifyOptions): string | number {
    return emit('warning', message, mergeOptions('warning', options));
  },

  info(message: string, options?: NotifyOptions): string | number {
    return emit('info', message, mergeOptions('info', options));
  },

  dismiss(id: string | number): void {
    toast.dismiss(id);
  },

  dismissAll(): void {
    toast.dismiss();
    dedupeMap.clear();
  },
};

export function notifyApiError(error: unknown, context?: string): string | number {
  const message = ApiExceptions.getErrorMessage(error as dynamicObj);
  if (context) {
    return notify.error(`${context}: ${String(message)}`, message.detail);
  }
  return notify.error(message);
}
